import {
  type OpenChangeReason,
  arrow,
  autoUpdate,
  flip,
  hide,
  inline,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import type { TooltipRootPropsInterface } from '../types/tooltip-root.types';
import type {
  TooltipFloatingContextInterface,
  UseTooltipReturnInterface,
} from '../types/use-tooltip.types';

export function useTooltip(props: Partial<TooltipRootPropsInterface>): {
  tooltip: UseTooltipReturnInterface;
  floating: TooltipFloatingContextInterface;
} {
  const {
    defaultOpen,
    offset: offsetValue = 22,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    overflowPadding = 8,
    placement = 'top',
    shouldCloseOnViewportLeave = false,
    shouldAwaitInteractionResponse = true,
    useClickProps,
    useDismissProps,
    useFocusProps,
    useHoverProps,
    useRoleProps,
  } = props;

  const arrowRef = useRef(null);

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, floatingStyles, middlewareData } = useFloating({
    placement,
    open,
    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },
    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Improves positioning for inline reference elements that span over multiple lines
      inline(),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackAxisSideDirection: 'start',
        padding: overflowPadding,
      }),

      // Provides positioning data for an arrow element inside the floating element
      arrow({ element: arrowRef, padding: overflowPadding }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const hover = useHover(context, {
    enabled: typeof controlledOpen !== 'boolean',
    delay: 250,
    ...useHoverProps,
  });

  const focus = useFocus(context, {
    enabled: typeof controlledOpen !== 'boolean',
    ...useFocusProps,
  });

  const click = useClick(context, {
    enabled: typeof controlledOpen === 'boolean',
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) =>
      !(event.target as HTMLElement).closest('[data-c11n-component="Tooltip"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'tooltip',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, styles: floatingTransitionStyles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : 250,
    initial: {
      opacity: 0,
      transform: 'scale(0.975)',
    },
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlled) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlled, setControlledOpen],
  );

  const tooltip = useMemo(
    () => ({
      // Refs
      arrowRef,
      refs,

      // Mounted state
      isMounted,

      // Controlled state
      isControlled,

      // Styles
      floatingStyles,
      floatingTransitionStyles,

      // Trigger and reference props
      getReferenceProps,

      middlewareData,
    }),
    [
      refs,
      isMounted,
      isControlled,
      floatingStyles,
      floatingTransitionStyles,
      getReferenceProps,
      middlewareData,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * Tooltip.Content and Tooltip.Arrow pay for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { tooltip, floating };
}
